/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-eq-null */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Callout, ExternalLink } from "@octopusdeploy/design-system-components";
import { ControlType, Permission } from "@octopusdeploy/octopus-server-client";
import type { ActionTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import ControlTypeSelector from "~/components/ControlType/ControlTypeSelector";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import MarkdownEditor from "~/components/form/MarkdownEditor/MarkdownEditor";
import { dropdownOptionsValidator, required } from "~/components/form/Validators";
import Note from "~/primitiveComponents/form/Note/Note";
import { DebounceText } from "~/primitiveComponents/form/Text/Text";
interface ProcessTemplateParameterEditorDialogProps {
    allParameters: ActionTemplateParameterResource[];
    parameter?: ActionTemplateParameterResource;
    onOk(parameter: ActionTemplateParameterResource): void;
    processTemplateLink?: string | undefined;
}
const parameterEditPermission = {
    permission: Permission.PlatformHubEdit,
};
const emptyParameter: ActionTemplateParameterResource = {
    Id: "",
    Links: {},
    Name: "",
    Label: "",
    HelpText: "",
    DisplaySettings: {},
};
export default function ProcessTemplateParameterEditorDialog(props: ProcessTemplateParameterEditorDialogProps) {
    const [model, setModel] = React.useState(props.parameter || emptyParameter);
    const [originalName] = React.useState<string | null>(model.Name);
    const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
    const showRenameWarning = () => {
        return originalName && originalName !== model.Name;
    };
    const updateModel = (newValues: Partial<ActionTemplateParameterResource>) => {
        setModel({
            ...model,
            ...newValues,
        });
    };
    const onOk = () => {
        const errors: Record<string, string> = {};
        if (!model.Name) {
            errors["Name"] = "Please provide a name for the parameter.";
        }
        if (props.allParameters.some((p) => p.Name === model.Name && p.Name !== originalName)) {
            errors["Name"] = "This parameter name already exists. Parameter names should be unique.";
        }
        if (!model.DisplaySettings["Octopus.ControlType"]) {
            errors["ControlType"] = "Please provide a control type for the parameter.";
        }
        const selectOptionsError = dropdownOptionsValidator()(model.DisplaySettings["Octopus.SelectOptions"]!);
        if (model.DisplaySettings["Octopus.ControlType"] === ControlType.Select && selectOptionsError !== "") {
            errors["SelectOptions"] = `Drop down options are not formatted correctly. ${selectOptionsError}.`;
        }
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return false;
        }
        props.onOk(model);
        return true;
    };
    return (<OkDialogLayout onOkClick={onOk} okButtonPermission={parameterEditPermission} title={props.parameter ? "Update Parameter" : "Add Parameter"} okButtonLabel={props.parameter ? "Update" : "Add"}>
            <div>
                {showRenameWarning() && (<Callout type={"warning"} title={"Potential data loss"}>
                        <div>
                            <p>If you rename a parameter, you'll lose any data associated with it in existing steps. You should ensure all usages are updated to use the new name.</p>
                            {props.processTemplateLink && (<p>
                                    We recommend renaming parameters via the <ExternalLink label={"template's source code"} href={props.processTemplateLink}/>.
                                </p>)}
                        </div>
                    </Callout>)}

                {!props.parameter && (<ControlTypeSelector excludedControlTypeOptions={[]} controlType={model.DisplaySettings["Octopus.ControlType"]} selectOptions={model.DisplaySettings["Octopus.SelectOptions"]} onControlTypeChange={(controlType) => updateModel({
                DisplaySettings: {
                    ...model.DisplaySettings,
                    ["Octopus.ControlType"]: controlType,
                },
            })} onSelectOptionsChange={(selectOptions) => updateModel({
                DisplaySettings: {
                    ...model.DisplaySettings,
                    ["Octopus.SelectOptions"]: selectOptions,
                },
            })} controlTypeError={validationErrors["ControlType"]} selectOptionsError={validationErrors["SelectOptions"]} hideDescription={true}/>)}

                <DebounceText value={model.Name} onChange={(Name) => updateModel({ Name })} label="Parameter name" error={validationErrors["Name"]} validate={required("Please enter a parameter name")}/>
                <Note>Can contain letters, digits, dashes and periods.</Note>

                <DebounceText value={model.Label} onChange={(Label) => updateModel({ Label })} label="Label (Optional)"/>
                <Note>
                    The parameter label shown in the project e.g. <code>Server name</code>.
                </Note>

                <MarkdownEditor value={model.HelpText} onChange={(HelpText) => updateModel({ HelpText })} label="Help text (Optional)"/>
                <Note>Additional instructions or context that would be helpful for this field.</Note>
            </div>
        </OkDialogLayout>);
}
